import {
  collection, doc, addDoc, updateDoc, deleteDoc, getDocs, getDoc, query,
  where, orderBy, limit, serverTimestamp, writeBatch, deleteField, onSnapshot,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';

// ─── Time Logs ────────────────────────────────────────────────────────────────

export async function checkIn(userId, userName, location, locationName = '', travelCost = 0, dailyExpenses = []) {
  const today = toDateStr(new Date());
  const existing = await getTodayLog(userId, today);
  if (existing) return { success: false, error: 'เช็คอินวันนี้ไปแล้ว' };

  const ref2 = await addDoc(collection(db, 'time_logs'), {
    userId,
    userName,
    date: today,
    location,
    locationName,
    checkIn: serverTimestamp(),
    checkOut: null,
    totalHours: null,
    status: 'pending',
    approvedBy: null,
    approvedAt: null,
    travelCost: Number(travelCost) || 0,
    fieldAllowance: 0,
    dailyExpenses: dailyExpenses.filter(e => e.label && Number(e.amount) > 0).map(e => ({ label: e.label, amount: Number(e.amount) })),
    note: '',
    createdAt: serverTimestamp(),
  });
  return { success: true, id: ref2.id };
}

export async function submitRetroLog(userId, userName, { date, checkInTime, checkOutTime, location, locationName = '', note = '', travelCost = 0, dailyExpenses = [] }) {
  const existing = await getTodayLog(userId, date);
  if (existing) {
    if (existing.status === 'rejected') {
      return resubmitLog(existing.id, { date, checkInTime, checkOutTime, location, locationName, note });
    }
    return { success: false, error: `มีบันทึกวันที่ ${date} แล้ว` };
  }

  const checkInDate  = new Date(`${date}T${checkInTime}:00`);
  const checkOutDate = new Date(`${date}T${checkOutTime}:00`);
  const totalHours = Math.round(((checkOutDate - checkInDate) / 3600000) * 100) / 100;

  if (totalHours <= 0) return { success: false, error: 'เวลาออกต้องมากกว่าเวลาเข้า' };

  const { Timestamp } = await import('firebase/firestore');
  const ref2 = await addDoc(collection(db, 'time_logs'), {
    userId,
    userName,
    date,
    location,
    locationName,
    checkIn:  Timestamp.fromDate(checkInDate),
    checkOut: Timestamp.fromDate(checkOutDate),
    totalHours,
    status: 'pending',
    approvedBy: null,
    approvedAt: null,
    travelCost: Number(travelCost) || 0,
    fieldAllowance: 0,
    dailyExpenses: dailyExpenses.filter(e => e.label && Number(e.amount) > 0).map(e => ({ label: e.label, amount: Number(e.amount) })),
    note,
    isRetro: true,
    createdAt: serverTimestamp(),
  });
  return { success: true, id: ref2.id };
}

export async function checkOut(logId, checkInTime) {
  const now = new Date();
  const checkInDate = checkInTime?.toDate ? checkInTime.toDate() : (checkInTime ? new Date(checkInTime) : null);
  const totalHours = checkInDate && !isNaN(checkInDate)
    ? Math.max(0, Math.round(((now - checkInDate) / 3600000) * 100) / 100)
    : 0;

  await updateDoc(doc(db, 'time_logs', logId), {
    checkOut: serverTimestamp(),
    totalHours,
  });
  return { success: true };
}

export async function getTodayLog(userId, dateStr) {
  const q = query(
    collection(db, 'time_logs'),
    where('userId', '==', userId),
    where('date', '==', dateStr)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
}

export async function getUserLogs(userId, limitDays = 30) {
  // Simple single-field filter — no composite index required; sort client-side
  const q = query(collection(db, 'time_logs'), where('userId', '==', userId));
  const snap = await getDocs(q);
  const since = toDateStr(new Date(Date.now() - limitDays * 86400000));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((d) => d.date >= since)
    .sort((a, b) => b.date.localeCompare(a.date));
}

export async function getAllPendingLogs() {
  const q = query(collection(db, 'time_logs'), where('status', '==', 'pending'));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => b.date.localeCompare(a.date));
}

export async function getAllLogsByMonth(year, month) {
  const start = `${year}-${String(month).padStart(2, '0')}-01`;
  const end   = `${year}-${String(month).padStart(2, '0')}-31`;
  const snap = await getDocs(collection(db, 'time_logs'));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((d) => d.date >= start && d.date <= end)
    .sort((a, b) => b.date.localeCompare(a.date));
}

export async function updateLogAllowances(logId, { travelCost, fieldAllowance, dailyExpenses }) {
  const data = {};
  if (travelCost     !== undefined) data.travelCost     = Number(travelCost)     || 0;
  if (fieldAllowance !== undefined) data.fieldAllowance = Number(fieldAllowance) || 0;
  if (dailyExpenses  !== undefined) data.dailyExpenses  = dailyExpenses.filter(e => e.label && Number(e.amount) > 0).map(e => ({ label: e.label, amount: Number(e.amount) }));
  await updateDoc(doc(db, 'time_logs', logId), data);
}

export async function approveLog(logId, adminName) {
  await updateDoc(doc(db, 'time_logs', logId), {
    status: 'approved',
    approvedBy: adminName,
    approvedAt: serverTimestamp(),
  });
}

export async function rejectLog(logId, adminName) {
  await updateDoc(doc(db, 'time_logs', logId), {
    status: 'rejected',
    approvedBy: adminName,
    approvedAt: serverTimestamp(),
  });
}

export async function resubmitLog(logId, { date, checkInTime, checkOutTime, location, locationName = '', note = '' }) {
  const { Timestamp } = await import('firebase/firestore');
  const checkInDate  = new Date(`${date}T${checkInTime}:00`);
  const checkOutDate = new Date(`${date}T${checkOutTime}:00`);
  const totalHours = Math.round(((checkOutDate - checkInDate) / 3600000) * 100) / 100;
  if (totalHours <= 0) return { success: false, error: 'เวลาออกต้องมากกว่าเวลาเข้า' };
  await updateDoc(doc(db, 'time_logs', logId), {
    checkIn:    Timestamp.fromDate(checkInDate),
    checkOut:   Timestamp.fromDate(checkOutDate),
    totalHours, location, locationName, note,
    status:     'pending',
    approvedBy: null,
    approvedAt: null,
    updatedAt:  serverTimestamp(),
  });
  return { success: true };
}

// ─── Leave Requests ───────────────────────────────────────────────────────────

export async function submitLeaveRequest(userId, userName, { type, startDate, endDate, days, reason }) {
  const ref2 = await addDoc(collection(db, 'leave_requests'), {
    userId, userName, type, startDate, endDate,
    days: Number(days),
    reason,
    status: 'pending',
    approvedBy: null,
    createdAt: serverTimestamp(),
  });
  return { success: true, id: ref2.id };
}

export async function getUserLeaveRequests(userId) {
  const q = query(collection(db, 'leave_requests'), where('userId', '==', userId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getAllPendingLeave() {
  const q = query(collection(db, 'leave_requests'), where('status', '==', 'pending'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getAllLeaves() {
  const snap = await getDocs(collection(db, 'leave_requests'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Real-time subscription — filters out rejected/cancelled automatically
export function subscribeLeaves(callback) {
  return onSnapshot(collection(db, 'leave_requests'), (snap) => {
    const data = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((l) => l.status !== 'rejected' && l.status !== 'cancelled');
    callback(data);
  });
}

// ─── Admin corrections on already-approved records ────────────────────────────
// Soft-cancel (status='cancelled') keeps the row for audit but drops it from
// every summary (which count only 'approved'). Reversible by re-approving.

export async function cancelLeaveRequest(leaveId, adminName) {
  await updateDoc(doc(db, 'leave_requests', leaveId), {
    status: 'cancelled',
    cancelledBy: adminName,
    cancelledAt: serverTimestamp(),
  });
}

export async function updateLeaveRequest(leaveId, { type, startDate, endDate, days }, adminName) {
  await updateDoc(doc(db, 'leave_requests', leaveId), {
    type, startDate, endDate, days: Number(days),
    editedBy: adminName,
    editedAt: serverTimestamp(),
  });
}

export async function cancelLog(logId, adminName) {
  await updateDoc(doc(db, 'time_logs', logId), {
    status: 'cancelled',
    cancelledBy: adminName,
    cancelledAt: serverTimestamp(),
  });
}

export async function adminUpdateLog(logId, { location, locationName = '', note = '' }, adminName) {
  await updateDoc(doc(db, 'time_logs', logId), {
    location, locationName, note,
    editedBy: adminName,
    editedAt: serverTimestamp(),
  });
}

// ─── Expense Claims ───────────────────────────────────────────────────────────

export async function submitExpenseClaim(userId, userName, { category, amount, description, date, project, receiptStatus = 'none' }, receiptFile) {
  let receiptUrl = null;
  let receiptFileName = null;

  if (receiptFile) {
    const storageRef = ref(storage, `receipts/${userId}/${Date.now()}_${receiptFile.name}`);
    await uploadBytes(storageRef, receiptFile);
    receiptUrl = await getDownloadURL(storageRef);
    receiptFileName = receiptFile.name;
  }

  const ref2 = await addDoc(collection(db, 'expense_claims'), {
    userId, userName, category,
    amount: Number(amount),
    description, date, project,
    receiptUrl, receiptFileName,
    receiptStatus: receiptStatus || 'none',
    status: 'pending',
    approvedBy: null,
    createdAt: serverTimestamp(),
  });
  return { success: true, id: ref2.id };
}

export async function getUserExpenseClaims(userId) {
  const q = query(collection(db, 'expense_claims'), where('userId', '==', userId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Approved on/before 10th → paid this month's 15th; after 10th → next month's 15th
function computePaymentCycle() {
  const now = new Date();
  const base = now.getDate() > 10
    ? new Date(now.getFullYear(), now.getMonth() + 1, 1)
    : now;
  return `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}`;
}

export async function approveExpense(claimId, adminName) {
  await updateDoc(doc(db, 'expense_claims', claimId), {
    status: 'approved', approvedBy: adminName, approvedAt: serverTimestamp(),
    paymentCycle: computePaymentCycle(),
  });
}

export async function rejectExpense(claimId, adminName) {
  await updateDoc(doc(db, 'expense_claims', claimId), {
    status: 'rejected', approvedBy: adminName, approvedAt: serverTimestamp(),
  });
}

// Undo an approval (only meaningful before it's transferred): back to pending.
export async function revertExpenseToPending(claimId, adminName) {
  await updateDoc(doc(db, 'expense_claims', claimId), {
    status: 'pending', approvedBy: null, approvedAt: null,
    paymentCycle: deleteField(), revertedBy: adminName, revertedAt: serverTimestamp(),
  });
}

export async function bulkMarkExpenseTransferred(claimIds, adminName, transferSlipFile, transferDate) {
  let transferSlipUrl = null;
  let transferSlipFileName = null;
  if (transferSlipFile) {
    const sRef = ref(storage, `transfer_slips/bulk_${Date.now()}_${transferSlipFile.name}`);
    await uploadBytes(sRef, transferSlipFile);
    transferSlipUrl = await getDownloadURL(sRef);
    transferSlipFileName = transferSlipFile.name;
  }
  await Promise.all(claimIds.map(id =>
    updateDoc(doc(db, 'expense_claims', id), {
      status: 'transferred',
      transferredBy: adminName,
      transferDate,
      transferSlipUrl,
      transferSlipFileName,
      transferredAt: serverTimestamp(),
    })
  ));
}

export async function markExpenseTransferred(claimId, adminName, transferSlipFile, transferDate) {
  let transferSlipUrl = null;
  let transferSlipFileName = null;
  if (transferSlipFile) {
    const sRef = ref(storage, `transfer_slips/${claimId}_${Date.now()}_${transferSlipFile.name}`);
    await uploadBytes(sRef, transferSlipFile);
    transferSlipUrl = await getDownloadURL(sRef);
    transferSlipFileName = transferSlipFile.name;
  }
  await updateDoc(doc(db, 'expense_claims', claimId), {
    status: 'transferred',
    transferredBy: adminName,
    transferDate,
    transferSlipUrl,
    transferSlipFileName,
    transferredAt: serverTimestamp(),
  });
}

export async function updateExpenseClaim(claimId, { category, amount, description, date, project }) {
  const data = {};
  if (category    !== undefined) data.category    = category;
  if (amount      !== undefined) data.amount      = Number(amount);
  if (description !== undefined) data.description = description;
  if (date        !== undefined) data.date        = date;
  if (project     !== undefined) data.project     = project;
  await updateDoc(doc(db, 'expense_claims', claimId), data);
}

export async function updateExpenseReceiptStatus(claimId, receiptStatus) {
  await updateDoc(doc(db, 'expense_claims', claimId), { receiptStatus });
}

// Remove a wrongly-uploaded receipt so a new one can be attached.
// Clears the reference only (the old file is left orphaned in Storage, harmless).
export async function clearExpenseReceipt(claimId) {
  await updateDoc(doc(db, 'expense_claims', claimId), {
    receiptUrl: deleteField(), receiptFileName: deleteField(), receiptStatus: 'none',
  });
}

// Attach a receipt file to an existing claim (e.g. one approved without a slip).
export async function uploadExpenseReceipt(claimId, userId, file) {
  const storageRef = ref(storage, `receipts/${userId}/${Date.now()}_${file.name}`);
  await uploadBytes(storageRef, file);
  const receiptUrl = await getDownloadURL(storageRef);
  await updateDoc(doc(db, 'expense_claims', claimId), {
    receiptUrl, receiptFileName: file.name, receiptStatus: 'receipt',
  });
  return { receiptUrl, receiptFileName: file.name };
}

// Hard-delete a not-yet-approved claim. Guarded here too so an approved/
// transferred claim (already in the accounting flow) can never be removed.
export async function deleteExpenseClaim(claimId) {
  const snap = await getDoc(doc(db, 'expense_claims', claimId));
  const status = snap.exists() ? snap.data().status : null;
  if (status === 'approved' || status === 'transferred') {
    return { success: false, error: 'ลบได้เฉพาะรายการที่ยังไม่อนุมัติ' };
  }
  await deleteDoc(doc(db, 'expense_claims', claimId));
  return { success: true };
}

export async function getAllExpenseClaims() {
  const snap = await getDocs(collection(db, 'expense_claims'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ─── Payment Vouchers ──────────────────────────────────────────────────────────

export async function createPaymentVoucher(adminId, adminName, staffId, staffName, items, extras = {}) {
  // Guard: reject if any expense already belongs to a signed voucher
  const alreadyIssued = items.filter(i => i.voucherId);
  if (alreadyIssued.length > 0) {
    throw new Error(`รายการต่อไปนี้ออกใบสำคัญแล้ว: ${alreadyIssued.map(i => i.description || i.id).join(', ')}`);
  }

  const month = items[0]?.date?.slice(0, 7) || new Date().toISOString().slice(0, 7);
  const voucherNo = extras.voucherNo || await getNextVoucherNo(month);
  const { voucherNo: _ignore, ...restExtras } = extras;
  const totalAmount = items.reduce((s, i) => s + Number(i.amount || 0), 0);

  const batch = writeBatch(db);

  // Create voucher document
  const voucherRef = doc(collection(db, 'payment_vouchers'));
  batch.set(voucherRef, {
    voucherNo, staffId, staffName, adminId, adminName, month,
    ...restExtras,
    items: items.map(i => ({
      expenseId: i.id,
      description: i.description || '',
      category: i.category || '',
      date: i.date || '',
      amount: Number(i.amount || 0),
      project: i.project || '',
      receiptStatus: i.receiptStatus || 'none',
    })),
    totalAmount,
    status: 'pending_signature',
    createdAt: serverTimestamp(),
    signedAt: null,
  });

  // Link each expense back to this voucher atomically
  for (const item of items) {
    batch.update(doc(db, 'expense_claims', item.id), {
      voucherId: voucherRef.id,
      voucherNo,
      voucherStatus: 'pending_signature',
    });
  }

  await batch.commit();
  return { success: true, id: voucherRef.id, voucherNo };
}

export async function getNextVoucherNo(month) {
  const { runTransaction } = await import('firebase/firestore');
  const counterRef = doc(db, 'receipt_counters', `PV-${month.replace('-', '')}`);

  // Fast path: counter already initialised
  const seq = await runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    if (snap.exists()) {
      const next = snap.data().seq + 1;
      tx.set(counterRef, { seq: next }, { merge: true });
      return next;
    }
    return null; // counter not yet seeded
  });

  if (seq !== null) {
    return `PV-${month.replace('-', '')}-${String(seq).padStart(3, '0')}`;
  }

  // One-time seeding: initialise counter from existing vouchers so numbers don't restart at 001
  const existing = await getDocs(query(collection(db, 'payment_vouchers'), where('month', '==', month)));
  const seedCount = existing.size;

  const seededSeq = await runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    if (snap.exists()) {
      // Another process seeded it while we were querying — just increment
      const next = snap.data().seq + 1;
      tx.set(counterRef, { seq: next }, { merge: true });
      return next;
    }
    const next = seedCount + 1;
    tx.set(counterRef, { seq: next });
    return next;
  });

  return `PV-${month.replace('-', '')}-${String(seededSeq).padStart(3, '0')}`;
}

export async function getUserProfile(userId) {
  const q = query(collection(db, 'user_profiles'), where('userId', '==', userId));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
}

export async function getAllUserProfiles() {
  const snap = await getDocs(collection(db, 'user_profiles'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function updateTalentProfile(userId, { firstName, lastName, phone }) {
  const q = query(collection(db, 'bizops_talent'), where('userId', '==', userId));
  const snap = await getDocs(q);
  if (snap.empty) return;
  const updates = {};
  if (phone !== undefined) updates.phone = phone;
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  if (fullName) updates.name = fullName;
  if (Object.keys(updates).length) {
    await updateDoc(doc(db, 'bizops_talent', snap.docs[0].id), updates);
  }
}

export async function saveUserProfile(userId, profileData) {
  const q = query(collection(db, 'user_profiles'), where('userId', '==', userId));
  const snap = await getDocs(q);
  const data = { userId, ...profileData, updatedAt: serverTimestamp() };
  if (snap.empty) {
    await addDoc(collection(db, 'user_profiles'), data);
  } else {
    await updateDoc(doc(db, 'user_profiles', snap.docs[0].id), data);
  }
}

export async function getVouchersByStaff(staffId) {
  const q = query(collection(db, 'payment_vouchers'), where('staffId', '==', staffId));
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.voucherNo || '').localeCompare(a.voucherNo || ''));
}

export async function getAllVouchers() {
  const snap = await getDocs(collection(db, 'payment_vouchers'));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.voucherNo || '').localeCompare(a.voucherNo || ''));
}

// Propagate voucher status back to every linked expense_claim
async function syncVoucherStatusToExpenses(voucherId, voucherStatus) {
  const vSnap = await getDoc(doc(db, 'payment_vouchers', voucherId));
  if (!vSnap.exists()) return;
  const batch = writeBatch(db);
  for (const item of (vSnap.data().items || [])) {
    if (item.expenseId) {
      batch.update(doc(db, 'expense_claims', item.expenseId), { voucherStatus });
    }
  }
  await batch.commit();
}

export async function signVoucher(voucherId) {
  await updateDoc(doc(db, 'payment_vouchers', voucherId), {
    status: 'signed',
    signedAt: serverTimestamp(),
  });
  await syncVoucherStatusToExpenses(voucherId, 'signed');
}

export async function setVoucherStatus(voucherId, status) {
  const data = { status };
  if (status === 'signed') data.signedAt = serverTimestamp();
  else data.signedAt = null;
  await updateDoc(doc(db, 'payment_vouchers', voucherId), data);
  await syncVoucherStatusToExpenses(voucherId, status);
}

export async function deletePaymentVoucher(voucherId) {
  const vSnap = await getDoc(doc(db, 'payment_vouchers', voucherId));
  const batch = writeBatch(db);
  if (vSnap.exists()) {
    for (const item of (vSnap.data().items || [])) {
      if (item.expenseId) {
        batch.update(doc(db, 'expense_claims', item.expenseId), {
          voucherId: deleteField(),
          voucherNo: deleteField(),
          voucherStatus: deleteField(),
        });
      }
    }
  }
  batch.delete(doc(db, 'payment_vouchers', voucherId));
  await batch.commit();
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// On-screen date format across the app: "27 Jul 2026".
export function formatDate(dateStr) {
  if (!dateStr) return '-';
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const [y, m, d] = dateStr.split('-');
  return `${parseInt(d)} ${months[parseInt(m) - 1]} ${y}`;
}

export function formatTimestamp(ts) {
  if (!ts) return '-';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}
